import React, { useMemo } from "react";
import { Box, SimpleGrid, VStack } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import type { ChartConfig } from "@/components/ui/chart";
import type { NotificationItem } from "../../types/notifications";
import { useDisplayCurrency } from "../../contexts/DisplayCurrencyContext";
import { useRiskMetrics } from "../../hooks/useTradeStatistics";
import {
  parsePerformanceSummaryViewModel,
  type PerformanceSummarySymbolRow,
} from "../../utils/performanceSummaryViewModel";
import { miniStatGridTemplateColumns } from "../../utils/miniStatGridLayout";
import TradeLogMiniStatistics from "../../views/Dashboard/TradeLog/components/TradeLogMiniStatistics";
import {
  DistributionPieLabelChart,
  type DistributionDonutCenter,
  type DistributionPieDatum,
} from "../../views/Dashboard/Statistics/components/DistributionPieLabelChart";
import DrawdownOverTimeChart from "../../views/Dashboard/Statistics/components/DrawdownOverTimeChart";
import {
  STATS_CHART_TOOLTIP_LABEL_CLASS,
  STATS_CHART_TOOLTIP_VALUE_CLASS,
} from "../../views/Dashboard/Statistics/components/statisticsChartTooltip";

export interface PerformanceSummaryMetricsGridProps {
  /** Structured payload from `NotificationItem.data` (same shape as modal `details`). */
  data: NotificationItem["data"];
  /** Tighter layout for list rows / narrow columns */
  compact?: boolean;
}

const DISTRIBUTION_POSITIVE_COLOR = "#06E363";
const DISTRIBUTION_NEGATIVE_COLOR = "#F70009";
const DISTRIBUTION_SCRATCH_COLOR = "#D3D3D3";

/** Tessera brand blues — same palette as Statistics symbol distribution. */
const BRAND_BLUE_PALETTE = ["#38BBD1", "#73E6F0", "#3EEAEC", "#96FBF7"] as const;

function toSegmentKey(label: string): string {
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return key || "slice";
}

function formatDominantPercent(pct: number): string {
  return pct >= 10 ? pct.toFixed(0) : pct.toFixed(1);
}

function buildTwoSlicePie(
  slices: Array<{ label: string; value: number; color: string }>
): {
  data: DistributionPieDatum[];
  config: ChartConfig;
  center: DistributionDonutCenter | undefined;
} {
  const config: ChartConfig = {};
  const entries = slices.filter((e) => e.value > 0);
  const data: DistributionPieDatum[] = entries.map((entry) => {
    const segment = toSegmentKey(entry.label);
    config[segment] = { label: entry.label, color: entry.color };
    return {
      segment,
      label: entry.label,
      trades: entry.value,
      fill: entry.color,
    };
  });

  const total = entries.reduce((sum, e) => sum + e.value, 0);
  let center: DistributionDonutCenter | undefined;
  if (total > 0 && entries.length > 0) {
    const best = entries.reduce((a, b) => (b.value > a.value ? b : a));
    const pct = (best.value / total) * 100;
    center = { title: best.label, value: `${formatDominantPercent(pct)}%` };
  }

  return { data, config, center };
}

function buildLongShortPie(long: number, short: number) {
  return buildTwoSlicePie([
    { label: "Long", value: long, color: DISTRIBUTION_POSITIVE_COLOR },
    { label: "Short", value: short, color: DISTRIBUTION_NEGATIVE_COLOR },
  ]);
}

function buildWinLossPie(wins: number, losses: number, scratch: number) {
  const pie = buildTwoSlicePie([
    { label: "Win", value: wins, color: DISTRIBUTION_POSITIVE_COLOR },
    { label: "Scratch", value: scratch, color: DISTRIBUTION_SCRATCH_COLOR },
    { label: "Loss", value: losses, color: DISTRIBUTION_NEGATIVE_COLOR },
  ]);
  // Match Statistics: center reflects win vs loss share (scratch ignored for title).
  const total = wins + losses + scratch;
  if (total > 0) {
    const winPct = (wins / total) * 100;
    const lossPct = (losses / total) * 100;
    pie.center =
      winPct >= lossPct
        ? { title: "Win", value: `${formatDominantPercent(winPct)}%` }
        : { title: "Loss", value: `${formatDominantPercent(lossPct)}%` };
  }
  return pie;
}

function buildSymbolPie(symbols: PerformanceSummarySymbolRow[]): {
  data: DistributionPieDatum[];
  config: ChartConfig;
  center: DistributionDonutCenter | undefined;
  pnlBySegment: Map<string, number>;
} {
  const config: ChartConfig = {};
  const pnlBySegment = new Map<string, number>();
  const data: DistributionPieDatum[] = symbols
    .filter((row) => row.count > 0)
    .map((row, index) => {
      const segment = toSegmentKey(row.symbol);
      const color = BRAND_BLUE_PALETTE[index % BRAND_BLUE_PALETTE.length];
      config[segment] = { label: row.symbol, color };
      pnlBySegment.set(segment, row.total_pnl);
      return {
        segment,
        label: row.symbol,
        trades: row.count,
        fill: color,
      };
    });

  const center: DistributionDonutCenter | undefined =
    data.length > 0
      ? { title: "Symbols traded", value: String(data.length) }
      : undefined;

  return { data, config, center, pnlBySegment };
}

/**
 * In-app performance summary detail: Trade Log mini stats, long/short + symbol
 * distribution donuts, and Statistics drawdown chart. Missing metrics stay visible
 * as N/A / no-data — sections are never hidden for empty payloads.
 */
const PerformanceSummaryMetricsGrid: React.FC<PerformanceSummaryMetricsGridProps> = ({
  data,
  compact = false,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const { resolvedTheme } = useTheme();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";

  const viewModel = useMemo(() => parsePerformanceSummaryViewModel(data), [data]);

  const riskEnabled = Boolean(viewModel.riskFilters);
  const {
    data: riskData,
    loading: riskLoading,
    error: riskError,
    errorAction: riskErrorAction,
  } = useRiskMetrics(viewModel.riskFilters ?? undefined, { enabled: riskEnabled });

  const longShortPie = useMemo(() => {
    const { long, short } = viewModel.longShort ?? { long: 0, short: 0 };
    return buildLongShortPie(long, short);
  }, [viewModel.longShort]);

  const winLossPie = useMemo(() => {
    const { wins, losses, scratch } = viewModel.winLoss ?? {
      wins: 0,
      losses: 0,
      scratch: 0,
    };
    return buildWinLossPie(wins, losses, scratch);
  }, [viewModel.winLoss]);

  const symbolPie = useMemo(() => buildSymbolPie(viewModel.symbols), [viewModel.symbols]);

  const miniStatsGridTemplateColumns = useMemo(
    () =>
      miniStatGridTemplateColumns(viewModel.miniStats, {
        maxColumns: 4,
        minColumns: 2,
        gapPx: compact ? 12 : 16,
        floor: compact ? 11 : undefined,
      }),
    [compact, viewModel.miniStats]
  );

  const maxDrawdown = riskData?.max_drawdown ?? viewModel.maxDrawdown ?? null;
  const maxDrawdownPercent = riskData?.max_drawdown_percent ?? null;
  const drawdownSeries = riskData?.drawdown_data ?? [];

  return (
    <VStack align="stretch" spacing={compact ? 4 : 5} w="100%" className={themeClass}>
      {viewModel.miniStats.length > 0 ? (
        <Box
          as="div"
          w="100%"
          minW={0}
          display="grid"
          // Columns follow container width; track min grows with longest label/value.
          gridTemplateColumns={miniStatsGridTemplateColumns}
          gap={compact ? "12px" : "16px"}
          data-mini-stats-grid
        >
          {viewModel.miniStats.map((stat) => (
            <Box key={stat.id} w="100%" minW={0}>
              <TradeLogMiniStatistics
                title={stat.title}
                amount={stat.amount}
                percentage={null}
                showPercentage={false}
                backgroundGradient={stat.backgroundGradient}
                tooltip={stat.tooltip}
                fitAmount
              />
            </Box>
          ))}
        </Box>
      ) : null}

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="100%">
        <DistributionPieLabelChart
          title="Distribution by Symbol"
          data={symbolPie.data}
          config={symbolPie.config}
          center={symbolPie.center}
          tooltipFooter={(segmentKey) => {
            const pnl = symbolPie.pnlBySegment.get(segmentKey);
            if (pnl === undefined) return null;
            return (
              <div className="flex items-center gap-2 pt-0.5">
                <span className={STATS_CHART_TOOLTIP_LABEL_CLASS}>P&L</span>
                <span className={STATS_CHART_TOOLTIP_VALUE_CLASS}>
                  {formatMoney(pnl, { showSign: true })}
                </span>
              </div>
            );
          }}
        />
        <DistributionPieLabelChart
          title="Long vs Short"
          data={longShortPie.data}
          config={longShortPie.config}
          center={longShortPie.center}
        />
        <DistributionPieLabelChart
          title="Win vs Loss"
          data={winLossPie.data}
          config={winLossPie.config}
          center={winLossPie.center}
        />
      </SimpleGrid>

      <DrawdownOverTimeChart
        drawdownData={drawdownSeries}
        maxDrawdown={maxDrawdown}
        maxDrawdownPercent={maxDrawdownPercent}
        loading={riskEnabled && riskLoading}
        error={riskEnabled ? riskError : null}
        errorAction={riskEnabled ? riskErrorAction : null}
        title="Max drawdown"
        description="Peak-to-trough decline over the summary period"
      />
    </VStack>
  );
};

export default PerformanceSummaryMetricsGrid;
